import axios from "axios";
import BaseService from "../BaseService";
import {Transform} from "@/lib/helper";
import {string} from "zod";

export interface BubbleConstainsItem {
    key: string
    constraint_type: string
    value: string
}

class BubbleService extends BaseService {
    private baseGetUrl
    private basePostUrl
    private bubbleObjSlug = 'obj'
    private bubbleWfSlug = 'wf'
    static constraint = {
        eq : 'equals',
        neq: 'not equal',
        empty: 'is_empty',
        not_empty: 'is_not_empty',
        gt: 'greater than',
        lt: 'less than',
        text_contains: 'text_contains',
        not_text_contains: 'not text contains'
    }

    constructor() {
        super('')
        this.configHeaders = {Authorization: `Bearer ${this.configEnv.bubble.apiKey}`}
        this.baseGetUrl = this.configEnv.bubble.baseUrl.get
        this.basePostUrl = this.configEnv.bubble.baseUrl.post

        this.http.interceptors.response.use(
            (response: any) => response.response,
            (error) => {
                const {response} = error;

                return Promise.reject(error);
            },
        );
    }

    private setBaseUrl = (apiType: string, url: any) => {
        return `${this.baseGetUrl}/${apiType}/${url}`
    }

    getWf = (url: string, configHeaders?: any) => {
        return this.get(
            this.setBaseUrl(this.bubbleWfSlug, url),
            configHeaders
        );
    };

    postWf = (url: string, data: any, configHeaders?: undefined) => {
        return this.post(
            this.setBaseUrl(this.bubbleWfSlug, url),
            data,
            configHeaders
        );
    };

    getObj = (url: string, configHeaders?: any) => {
        return this.get(
            this.setBaseUrl(this.bubbleObjSlug, url),
            configHeaders
        );
    };

    postObj = (url: string, data: string, configHeaders?: any) => {
        return this.post(
            this.setBaseUrl(this.bubbleObjSlug, url),
            data,
            configHeaders
        );
    };

    postWfNoAuth = (url: string, data: any) => {
        return axios.post(
            this.setBaseUrl(this.bubbleWfSlug, url),
            data
        )
    }

    postObjNoAuth = (url: string, data: any) => {
        return axios.post(
            this.setBaseUrl(this.bubbleObjSlug, url),
            data
        )
    }

    postObjWithoutAuth = async (url: string, data: any,config?:any) => {
        const result = await axios.post(
            this.setBaseUrl(this.bubbleObjSlug, url),
            data,
            config
        )
        return result.data
    }

    patchObjWithoutAuth = async (url: string, data: any,config?:any) => {
        const result = await axios.patch(
            this.setBaseUrl(this.bubbleObjSlug, url),
            data,
            config
        )
        return result.data
    }

    getObjWithoutAuth = async (url: string) => {
        const result: any = await axios.get(this.setBaseUrl(this.bubbleObjSlug, url));
        return result.data.response
    }

    getListObj = async (typeName: string, currentPage: number | string = 1, pageSize: number | string = 10, conditions?: BubbleConstainsItem[] ) => {
        let conditionQuery:BubbleConstainsItem[] | string | undefined = conditions?.map(item => {
            return {
                key: item.key,
                constraint_type: item.constraint_type,
                value: item.value,
            }
        })
        conditionQuery = JSON.stringify(conditionQuery)
        let cursor = parseInt(currentPage.toString()) - 1;
        let limit = parseInt(pageSize.toString());
        let queryString = `cursor=${cursor}&limit=${limit}${conditionQuery ? `&constraints=${conditionQuery}` : ''}`
        return await this.getObjWithoutAuth(`${typeName}?${queryString}`)
    }

    getConfig = async (path: string) => {
        let constraint: any = ''
        constraint = [{key: 'config', constraint_type: 'equals', value: path}]
        constraint = JSON.stringify(constraint)
        let queryString = `${constraint ? `&constraints=${constraint}` : ''}`
        const result: any = await this.getObjWithoutAuth(`Core_Config_Data?${queryString}`)
        return result.results[0].value
    }

    uploadFile = async (file: any) => {
        const transform = new Transform()
        const fileBase64 = await transform.prepareDataForBubbleImageUpload(file)
        const result = await this.postWfNoAuth('upload_file',{file: fileBase64})
        return result.data.response.url
    }

    uploadMultipleFile = async (files:any) => {
        let fileList:any[] = []
        for (let file of files) {
            const newFile = await this.uploadFile(file)
            fileList = [...fileList, newFile]
        }
        return fileList
    }
}

export default BubbleService;
